import React, { useEffect, useState } from 'react';
import { Mail, Plus, RefreshCw, XCircle } from 'lucide-react';
import { invitationApi } from '@/lib/api/invitationApi';
import { Invitation, InvitationStatusLabels } from '@/lib/models/invitation';
import { StatusBadge } from '@/components/custom-ui/status-badge';
import { ActionButton } from '@/components/custom-ui/action-button';
import { IconChip } from '@/components/custom-ui/IconChip';
import { ConfirmDialog } from '@/components/custom-ui/ConfirmDialog';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_ROW } from '@/lib/nexilink-card-styles';
import SendInvitationModal from './SendInvitationModal';

// Initials for a registered recipient's avatar fallback.
const initialsOf = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
};

// InvitationsPage — a COMPACT "Väntande inbjudningar" section shown above the
// employee table (in MedarbetareArea). Only OUTSTANDING invites (pending /
// expired) appear here — accepted ones are redundant (that person is already an
// employee below) and cancelled ones are noise. Always offers "Ny inbjudan".
const InvitationsPage: React.FC = () => {
    const { toast } = useToast();
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showSendModal, setShowSendModal] = useState(false);
    const [resendTarget, setResendTarget] = useState<Invitation | null>(null);
    const [cancelTarget, setCancelTarget] = useState<Invitation | null>(null);
    const [isBusy, setIsBusy] = useState(false);

    useEffect(() => {
        loadInvitations();
    }, []);

    const loadInvitations = async () => {
        setIsLoading(true);
        try {
            const result = await invitationApi.getMyInvitations(1, 50);
            setInvitations(result.invitations);
        } finally {
            setIsLoading(false);
        }
    };

    // Resend + cancel both fire a real email/notification, so each is gated
    // behind a confirm dialog and reports the outcome via a toast.
    const confirmResend = async () => {
        if (!resendTarget) return;
        setIsBusy(true);
        try {
            await invitationApi.resendInvitation(resendTarget.id);
            toast({
                title: 'Inbjudan skickad',
                description: `En ny inbjudan skickades till ${resendTarget.email}.`,
            });
            setResendTarget(null);
            await loadInvitations();
        } catch (err) {
            toast({
                title: 'Kunde inte skicka inbjudan',
                description: err instanceof Error ? err.message : 'Försök igen senare.',
                variant: 'destructive',
            });
        } finally {
            setIsBusy(false);
        }
    };

    const confirmCancel = async () => {
        if (!cancelTarget) return;
        setIsBusy(true);
        try {
            await invitationApi.cancelInvitation(cancelTarget.id);
            toast({
                title: 'Inbjudan avbruten',
                description: `Inbjudan till ${cancelTarget.email} har avbrutits.`,
            });
            setCancelTarget(null);
            await loadInvitations();
        } catch (err) {
            toast({
                title: 'Kunde inte avbryta inbjudan',
                description: err instanceof Error ? err.message : 'Försök igen senare.',
                variant: 'destructive',
            });
        } finally {
            setIsBusy(false);
        }
    };

    // Outstanding only — accepted/cancelled are dropped (accepted = already an
    // employee in the table below).
    const outstanding = invitations.filter((i) => i.status === 'pending' || i.status === 'expired');

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-gray-900">
                    Väntande inbjudningar{outstanding.length > 0 ? ` (${outstanding.length})` : ''}
                </h2>
                <ActionButton icon={Plus} onClick={() => setShowSendModal(true)}>
                    Ny inbjudan
                </ActionButton>
            </div>

            {outstanding.length > 0 ? (
                <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                    <div className="px-2 py-2 divide-y divide-gray-50">
                        {outstanding.map((inv) => (
                            <div key={inv.id} className={NEXILINK_ROW}>
                                {/* Registered recipients get an avatar + name; email-only
                                    invites (not yet a user) fall back to a mail icon. */}
                                {inv.recipient_name ? (
                                    <Avatar className="w-10 h-10 ring-2 ring-white shadow-sm flex-shrink-0">
                                        <AvatarImage src={resolveMediaUrl(inv.recipient_picture_url)} alt={inv.recipient_name} />
                                        <AvatarFallback className="bg-[#1A4D4F] text-[#f5f5f5] text-xs font-semibold">
                                            {initialsOf(inv.recipient_name)}
                                        </AvatarFallback>
                                    </Avatar>
                                ) : (
                                    <IconChip icon={Mail} />
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-gray-900 truncate">
                                        {inv.recipient_name || inv.email}
                                    </p>
                                    <p className="text-xs text-gray-400 truncate">
                                        {inv.recipient_name && <span>{inv.email} · </span>}
                                        Skickad {new Date(inv.invited_at).toLocaleDateString('sv-SE')}
                                        {inv.status === 'pending' && (
                                            <span> · Giltig till {new Date(inv.expires_at).toLocaleDateString('sv-SE')}</span>
                                        )}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <StatusBadge status={inv.status} label={InvitationStatusLabels[inv.status]} />
                                    <button
                                        onClick={() => setResendTarget(inv)}
                                        className="p-2 text-[#1A4D4F] hover:bg-[#A4C3B2]/30 rounded-lg transition-colors"
                                        title={inv.status === 'expired' ? 'Skicka igen (förnyar giltigheten)' : 'Skicka igen'}
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                    </button>
                                    {inv.status === 'pending' && (
                                        <button
                                            onClick={() => setCancelTarget(inv)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Avbryt"
                                        >
                                            <XCircle className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                !isLoading && <p className="text-sm text-gray-400">Inga väntande inbjudningar just nu.</p>
            )}

            {showSendModal && (
                <SendInvitationModal
                    onClose={() => setShowSendModal(false)}
                    onSuccess={() => {
                        setShowSendModal(false);
                        loadInvitations();
                    }}
                />
            )}

            {/* Resend — confirm because it fires a real notification + email. */}
            <ConfirmDialog
                open={!!resendTarget}
                onOpenChange={(open) => { if (!open) setResendTarget(null); }}
                title="Skicka inbjudan igen?"
                description={
                    <>
                        En ny inbjudan skickas till <strong>{resendTarget?.email}</strong> — både som en
                        notis i appen och som ett e-postmeddelande
                        {resendTarget?.status === 'expired' ? ', och giltighetstiden förnyas' : ''}. Vill du fortsätta?
                    </>
                }
                confirmLabel="Skicka igen"
                onConfirm={confirmResend}
                isBusy={isBusy}
            />

            {/* Cancel — destructive; revokes the invite link. */}
            <ConfirmDialog
                open={!!cancelTarget}
                onOpenChange={(open) => { if (!open) setCancelTarget(null); }}
                title="Avbryt inbjudan?"
                description={
                    <>
                        Inbjudan till <strong>{cancelTarget?.email}</strong> återkallas och länken slutar
                        fungera. Vill du fortsätta?
                    </>
                }
                confirmLabel="Avbryt inbjudan"
                destructive
                onConfirm={confirmCancel}
                isBusy={isBusy}
            />
        </div>
    );
};

export default InvitationsPage;
